import { useEffect, useState, type FormEvent } from "react";
import { useSession, type Settings as SettingsModel } from "../state/session.js";
import { useT } from "../i18n/index.js";
import { api } from "../api/http.js";
import { Button } from "../ui/Button.js";
import { Panel } from "../ui/Panel.js";
import { toast } from "../ui/Toast.js";
import { errorKey } from "./Login.js";
import { SocialSettings } from "../social/SocialSettings.js";
import { NotificationSettings } from "../events/NotificationSettings.js";
import { AudioControls } from "../audio/AudioControls.js";
import { VersionPanel } from "../admin/VersionPanel.js";
import { GmBadge } from "../social/GmBadge.js";
import { Link } from "../router.js";
import { AsyncToggle } from "../ui/AsyncToggle.js";
import { AUTO_COVER_VIP } from "../base/panels/PayAction.js";
import { InvitePanel } from "./InvitePanel.js";

interface ApiKey {
  id: string;
  label: string;
  prefix: string;
  scopes: string[];
  createdAt: number;
  lastUsedAt: number | null;
  revokedAt: number | null;
}

interface SessionRow {
  id: string;
  createdAt: number;
  lastUsedAt: number;
  userAgent: string;
  current: boolean;
}

const SCOPES = ["read", "play", "chat", "store"] as const;

export function Settings() {
  const t = useT();
  const settings = useSession((s) => s.settings);
  const vip = useSession((s) => s.vip?.level ?? 0);
  const account = useSession((s) => s.account);
  const updateSettings = useSession((s) => s.updateSettings);
  const logout = useSession((s) => s.logout);
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [sessions, setSessions] = useState<SessionRow[]>([]);
  const [newKey, setNewKey] = useState<string | null>(null);
  const [label, setLabel] = useState("");
  const [scopes, setScopes] = useState<string[]>(["read", "play"]);
  const [pw, setPw] = useState({ current: "", next: "" });
  const [pwError, setPwError] = useState<string | null>(null);

  const reload = async () => {
    const [k, s] = await Promise.all([api<{ keys: ApiKey[] }>("GET", "/api/auth/api-keys"), api<{ sessions: SessionRow[] }>("GET", "/api/auth/sessions")]);
    setKeys(k.keys);
    setSessions(s.sessions);
  };
  useEffect(() => {
    void reload();
  }, []);

  const save = async (patch: Partial<SettingsModel>) => {
    await updateSettings(patch);
    toast.success(t("settings.saved"));
  };

  const createKey = async (e: FormEvent) => {
    e.preventDefault();
    const res = await api<{ key: string }>("POST", "/api/auth/api-keys", { label: label || "agent", scopes });
    setNewKey(res.key);
    setLabel("");
    await reload();
  };

  const revoke = async (id: string) => {
    await api("DELETE", `/api/auth/api-keys/${id}`);
    await reload();
  };

  const changePassword = async (e: FormEvent) => {
    e.preventDefault();
    setPwError(null);
    try {
      await api("POST", "/api/auth/password", pw);
      setPw({ current: "", next: "" });
      toast.success(t("settings.password.changed"));
    } catch (err) {
      setPwError(t(errorKey(err)));
    }
  };

  if (!settings) return null;
  return (
    <div style={{ maxWidth: 560, margin: "0 auto", padding: "0 12px 24px", display: "grid", gap: 12 }} data-testid="settings">
      {account?.role === "admin" ? <Panel title={<><GmBadge role={account.role} /> {t("settings.gm")}</>}><Link to="/app/admin" className="btn primary" data-testid="settings-gm">{t("settings.gm.open")}</Link></Panel> : null}
      <VersionPanel />
      <Panel title={t("settings.title")}>
        {account?.mustChangePassword ? <p className="error">{t("auth.mustChange")}</p> : null}
        <div className="field">
          <label htmlFor="set-locale">{t("settings.language")}</label>
          <select id="set-locale" value={settings.locale} onChange={(e) => void save({ locale: e.target.value as "de" | "en" })} data-testid="locale-select">
            <option value="de">{t("settings.language.de")}</option>
            <option value="en">{t("settings.language.en")}</option>
          </select>
        </div>
        <div className="field">
          <label htmlFor="set-gfx">{t("settings.graphics")}</label>
          <select id="set-gfx" value={settings.graphicsQuality} onChange={(e) => void save({ graphicsQuality: e.target.value as SettingsModel["graphicsQuality"] })}>
            {(["auto", "low", "medium", "high"] as const).map((q) => (
              <option key={q} value={q}>
                {t(`settings.graphics.${q}`)}
              </option>
            ))}
          </select>
        </div>
        <label className="row" style={{ marginBottom: 8 }}>
          <input type="checkbox" checked={settings.reducedMotion} onChange={(e) => void save({ reducedMotion: e.target.checked })} />
          {t("settings.reducedMotion")}
        </label>
        <AsyncToggle checked={settings.announcements} save={announcements => save({ announcements })} testId="announcements-enabled">{t("settings.announcements")}<span className="hint" style={{ display: "block" }}>{t("settings.announcements.hint")}</span></AsyncToggle>
      </Panel>

      <Panel title={t("settings.automation")}>
        <AsyncToggle checked={settings.autoEquipUnits} save={(autoEquipUnits) => save({ autoEquipUnits })} testId="auto-equip-units">{t("settings.autoEquipUnits")}</AsyncToggle>
        <AsyncToggle checked={settings.autoEquipModules} save={(autoEquipModules) => save({ autoEquipModules })} testId="auto-equip-modules">{t("settings.autoEquipModules")}</AsyncToggle>
        {vip >= AUTO_COVER_VIP ? (
          <AsyncToggle checked={settings.autoCoverCosts} save={(autoCoverCosts) => save({ autoCoverCosts })} testId="auto-cover-costs">{t("settings.autoCoverCosts")}</AsyncToggle>
        ) : (
          <p className="hint" data-testid="auto-cover-costs-locked">{t("settings.autoCoverCosts")} · {t("cover.vipHint", { n: AUTO_COVER_VIP })}</p>
        )}
      </Panel>

      <Panel title={t("settings.audio")}>
        <AudioControls settings={settings} save={save} />
      </Panel>

      <Panel title={t("settings.password")}>
        <form onSubmit={changePassword}>
          <div className="field">
            <label htmlFor="pw-current">{t("settings.password.current")}</label>
            <input id="pw-current" type="password" autoComplete="current-password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} required />
          </div>
          <div className="field">
            <label htmlFor="pw-next">{t("settings.password.next")}</label>
            <input id="pw-next" type="password" autoComplete="new-password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} required minLength={8} />
          </div>
          {pwError ? <p className="error">{pwError}</p> : null}
          <Button type="submit">{t("common.save")}</Button>
        </form>
      </Panel>

      <InvitePanel />

      <Panel title={t("settings.apiKeys")}>
        <p className="hint" style={{ marginTop: 0 }}>{t("settings.apiKeys.hint")}</p>
        {newKey ? (
          <div className="glass" style={{ padding: 10, marginBottom: 12, borderColor: "rgba(245,197,66,0.5)" }}>
            <div className="hint">{t("settings.apiKeys.new")}</div>
            <code className="mono" style={{ wordBreak: "break-all", userSelect: "all" }} data-testid="new-api-key">
              {newKey}
            </code>
          </div>
        ) : null}
        <form onSubmit={createKey} className="row" style={{ flexWrap: "wrap", marginBottom: 12 }}>
          <input placeholder={t("settings.apiKeys.label")} value={label} onChange={(e) => setLabel(e.target.value)} style={{ minHeight: 40, padding: "0 10px", borderRadius: 8, border: "1px solid var(--line-strong)", background: "rgba(0,0,0,0.35)" }} data-testid="api-key-label" />
          <span className="row" style={{ gap: 6, fontSize: 12 }}>
            {SCOPES.map((s) => (
              <label key={s} className="row" style={{ gap: 3 }}>
                <input type="checkbox" checked={scopes.includes(s)} onChange={(e) => setScopes(e.target.checked ? [...scopes, s] : scopes.filter((x) => x !== s))} />
                {s}
              </label>
            ))}
          </span>
          <Button type="submit" variant="primary" disabled={scopes.length === 0} data-testid="api-key-create">
            {t("settings.apiKeys.create")}
          </Button>
        </form>
        {keys.length === 0 ? <p className="dim">{t("settings.apiKeys.none")}</p> : null}
        {keys.map((k) => (
          <div key={k.id} className="row" style={{ padding: "6px 0", borderTop: "1px solid var(--line)", opacity: k.revokedAt ? 0.5 : 1 }}>
            <span className="grow">
              <strong>{k.label}</strong> <span className="mono dim">zk_{k.prefix}…</span>
              <div className="hint">
                {t("settings.apiKeys.scopes")}: {k.scopes.join(", ")}
              </div>
            </span>
            {k.revokedAt ? null : (
              <Button variant="danger" onClick={() => void revoke(k.id)}>
                {t("settings.apiKeys.revoke")}
              </Button>
            )}
          </div>
        ))}
      </Panel>

      <Panel title={t("settings.sessions")}>
        {sessions.map((s) => (
          <div key={s.id} className="row" style={{ padding: "6px 0", borderTop: "1px solid var(--line)", fontSize: 13 }}>
            <span className="grow">
              <span className="mono">{s.id}</span> {s.current ? <span style={{ color: "var(--gold)" }}>({t("settings.sessions.current")})</span> : null}
              <div className="hint">{s.userAgent.slice(0, 60)}</div>
            </span>
            <span className="dim">{new Date(s.lastUsedAt).toLocaleString()}</span>
          </div>
        ))}
        <div className="row" style={{ marginTop: 12, gap: 8 }}>
          <Button onClick={() => void logout(false)} data-testid="logout">
            {t("auth.logout")}
          </Button>
          <Button variant="danger" onClick={() => void logout(true)}>
            {t("auth.logoutAll")}
          </Button>
        </div>
      </Panel>

      <NotificationSettings />
      <SocialSettings settings={settings} save={save} />
    </div>
  );
}
